import { createContext, useContext, useReducer } from 'react'
import PropTypes from 'prop-types'
import R from 'ramda'

import getInitialState from 'Common/utils/getInitialState'
import { LOC_LIST } from '../common/constants'

// CONTEXT OBJECTS
const OnboardingStudentState = createContext()
OnboardingStudentState.displayName = 'OnboardingStudentState'

const OnboardingStudentDispatch = createContext()
OnboardingStudentDispatch.displayName = 'OnboardingStudentDispatch'

const ACTIONS = {
  setIsInvitation: 'set is invitation',
  setLocale: 'set locale',
  setYumiClass: 'set yumiClass',
  setCurrentStep: 'set current step',
  setFirstName: 'set first name',
  setLastName: 'set last name',
  setPassword: 'set password',
  setEmail: 'set email',
  setRemoteErrors: 'set remote errors',
  setClassCode: 'set class code',
  setSocialSso: 'set social sso',
  setIsSmallBreakpoint: 'set is small breakpoint',
  setStepsLength: 'set steps length',
  resetState: 'reset state',
}

// ACTION CREATORS
const setIsSmallBreakpoint = isSm => ({
  type: ACTIONS.setIsSmallBreakpoint,
  payload: isSm,
})

const setIsInvitation = isInvitation => ({
  type: ACTIONS.setIsInvitation,
  payload: isInvitation,
})

const setClassCode = classCode => ({
  type: ACTIONS.setClassCode,
  payload: classCode,
})

const setLocale = lang => ({
  type: ACTIONS.setLocale,
  payload: lang,
})

const setYumiClass = yumiClass => ({
  type: ACTIONS.setYumiClass,
  payload: yumiClass,
})

const setCurrentStep = step => ({
  type: ACTIONS.setCurrentStep,
  payload: step,
})

const setFirstName = firstName => ({
  type: ACTIONS.setFirstName,
  payload: firstName,
})

const setLastName = lastName => ({
  type: ACTIONS.setLastName,
  payload: lastName,
})

const setPassword = password => ({
  type: ACTIONS.setPassword,
  payload: password,
})

const setEmail = email => ({
  type: ACTIONS.setEmail,
  payload: email,
})

const setRemoteErrors = errors => ({
  type: ACTIONS.setRemoteErrors,
  payload: errors,
})

const setSocialSso = social => ({
  type: ACTIONS.setSocialSso,
  payload: social,
})

const setStepsLength = steps => ({
  type: ACTIONS.setStepsLength,
  payload: steps,
})

const resetState = () => ({
  type: ACTIONS.resetState,
  payload: null,
})

function reducer(state, action) {
  const { type, payload } = action

  return R.cond([
    [
      R.equals(ACTIONS.setIsSmallBreakpoint),
      () => {
        return {
          ...state,
          isSm: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setIsInvitation),
      () => {
        return {
          ...state,
          isInvitation: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setClassCode),
      () => {
        return {
          ...state,
          classCode: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setLocale),
      () => {
        if (payload === 'english') {
          return {
            ...state,
            locale: null,
          }
        }
        return {
          ...state,
          locale: LOC_LIST.find(loc => loc.lang === payload).lang,
        }
      },
    ],
    [
      R.equals(ACTIONS.setYumiClass),
      () => {
        return {
          ...state,
          yumiClass: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setCurrentStep),
      () => {
        return {
          ...state,
          currentStep: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setFirstName),
      () => {
        return {
          ...state,
          firstName: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setLastName),
      () => {
        return {
          ...state,
          lastName: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setPassword),
      () => {
        return {
          ...state,
          password: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setEmail),
      () => {
        return {
          ...state,
          email: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setRemoteErrors),
      () => {
        return {
          ...state,
          remoteErrors: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setSocialSso),
      () => {
        return {
          ...state,
          socialSso: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.setStepsLength),
      () => {
        return {
          ...state,
          stepsLength: payload,
        }
      },
    ],
    [
      R.equals(ACTIONS.resetState),
      () => {
        return {
          ...state,
          isInvitation: null,
          classCode: null,
          currentStep: null,
          locale: null,
          yumiClass: null,
          firstName: null,
          lastName: null,
          email: null,
          password: null,
          remoteErrors: null,
          socialSso: null,
          stepsLength: null,
        }
      },
    ],
    [
      R.T,
      () => {
        throw new Error(`Unhandled action type: ${type}`)
      },
    ],
  ])(type)
}

// CUSTOM HOOK
function useStudentOnboardingContext() {
  const state = useContext(OnboardingStudentState)
  const dispatch = useContext(OnboardingStudentDispatch)

  if (state === undefined || dispatch === undefined)
    throw new Error(
      'useStudentOnboardingContext must be used within a StudentOnboardingProvider'
    )
  return [state, dispatch]
}

// CONTEXT PROVIDER
function StudentOnboardingProvider({ children }) {
  const initialState = getInitialState()
  const [state, setState] = useReducer(reducer, {
    isSm: null,
    isInvitation: null,
    classCode: null,
    currentStep: null,
    locale: null,
    yumiClass: null,
    firstName: null,
    lastName: null,
    email: null,
    password: null,
    remoteErrors: null,
    socialSso: null,
    stepsLength: null,
    ...initialState,
  })

  return (
    <OnboardingStudentState.Provider value={state}>
      <OnboardingStudentDispatch.Provider value={setState}>
        {children}
      </OnboardingStudentDispatch.Provider>
    </OnboardingStudentState.Provider>
  )
}

StudentOnboardingProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export {
  StudentOnboardingProvider,
  resetState,
  setClassCode,
  setCurrentStep,
  setEmail,
  setFirstName,
  setIsInvitation,
  setIsSmallBreakpoint,
  setLastName,
  setLocale,
  setPassword,
  setRemoteErrors,
  setSocialSso,
  setStepsLength,
  setYumiClass,
  useStudentOnboardingContext,
}
